import React, { useMemo, useState } from 'react';
import {
	Box,
	Button,
	ColumnLayout,
	Container,
	ContentLayout,
	Grid,
	Header,
	Link,
	SpaceBetween,
} from '@amzn/awsui-components-console';
import Table, { TableProps } from '@amzn/awsui-components-console/table';
import TextFilter from '@amzn/awsui-components-console/text-filter';
import Pagination from '@amzn/awsui-components-console/pagination';
import CollectionPreferences, {
	CollectionPreferencesProps,
} from '@amzn/awsui-components-console/collection-preferences';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { TaskDefinition } from 'src/stores/workflowDefinitionCanvasStore/workflowDefinitionCanvasStore.interface';
import { ValidationDefinition } from '@amzn/snow-nxt-workflow-service-typescript-client';
import { isTerminalTaskId } from 'src/components/createWorklow/customNode/customNodeEnums';
import {
	createTaskColumns,
	DEFAULT_PREFERENCES,
	COLLECTION_PREFERENCES,
} from 'src/pages/ViewWorkflow/ViewWorkflowConstants';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';
import TaskDetailsReviewPanel from 'src/components/createWorklow/taskDetailsReviewPanel/taskDetailsReviewPanel';
import CopyableText from 'src/components/findEntity/CopyableText';

export interface ViewWorkflowTasksTableProps {
	taskDefinitions: TaskDefinition[];
	extraColumns?: TableProps.ColumnDefinition<TaskDefinition>[];
	defaultPreferences?: CollectionPreferencesProps.Preferences;
	collectionPreferences?: Partial<CollectionPreferencesProps>;
}

export default function ViewWorkflowTasksTable({
	taskDefinitions,
	extraColumns = [],
	defaultPreferences = DEFAULT_PREFERENCES,
	collectionPreferences = COLLECTION_PREFERENCES,
}: ViewWorkflowTasksTableProps) {
	const [selectedTask, setSelectedTask] = useState<TaskDefinition | undefined>();
	const [selectedValidations, setSelectedValidations] = useState<ValidationDefinition[] | null>(null);
	const [selectedValidationTaskName, setSelectedValidationTaskName] = useState('');

	const actualTasks = useMemo(() => {
		return taskDefinitions.filter((task) => !isTerminalTaskId(task.taskId));
	}, [taskDefinitions]);

	const [preferences, setPreferences] =
		useState<CollectionPreferencesProps.Preferences>(() => ({
			...defaultPreferences,
			stripedRows: getDefaultStripedRows(),
		}));

	const TASK_COLUMNS = useMemo(
		() => {
			const baseColumns = createTaskColumns((task?: TaskDefinition) => {
				setSelectedTask(task);
				setSelectedValidations(null);
			});
			if (extraColumns.length > 0) {
				return [...baseColumns.slice(0, -1), ...extraColumns, baseColumns[baseColumns.length - 1]];
			}
			return baseColumns;
		},
		[extraColumns]
	);

	const columnsWithClickableValidation = useMemo(() => {
		return TASK_COLUMNS.map(col => {
			if (col.id === 'validationDefinitions') {
				return {
					...col,
					cell: (item: TaskDefinition) => {
						const validations: ValidationDefinition[] = item.validationDefinitions || [];
						if (validations.length === 0) return '-';
						return (
							<SpaceBetween size="xxs">
								{validations.map((v, i) => (
									<Link
										key={i}
										variant="primary"
										onFollow={() => {
											setSelectedValidationTaskName(item.taskName);
											setSelectedValidations([v]);
											setSelectedTask(undefined);
										}}
									>
										{v.validationName || v.validationId || 'Unnamed'}
									</Link>
								))}
							</SpaceBetween>
						);
					},
				};
			}
			return col;
		});
	}, [TASK_COLUMNS]);

	const {
		items,
		filteredItemsCount,
		collectionProps,
		filterProps,
		paginationProps,
	} = useCollection(actualTasks, {
		filtering: {},
		pagination: { pageSize: preferences.pageSize },
		sorting: {},
	});

	const showPanel = selectedTask || selectedValidations;

	return (
		<Container>
			<Grid
				gridDefinition={[
					{ colspan: showPanel ? 8 : 12 },
					{ colspan: showPanel ? 4 : 0 },
				]}
			>
				<Table
					{...collectionProps}
					columnDefinitions={columnsWithClickableValidation}
					items={items}
					variant="embedded"
					stickyHeader
					wrapLines={preferences.wrapLines}
					stripedRows={preferences.stripedRows}
					columnDisplay={preferences.contentDisplay}
					header={
						<Header
							variant="h2"
							counter={`(${filteredItemsCount})`}
							actions={
								<SpaceBetween direction="horizontal" size="xs">
									<TextFilter
										{...filterProps}
										filteringPlaceholder="Search tasks..."
										countText={`${filteredItemsCount} ${
											filteredItemsCount === 1 ? 'match' : 'matches'
										}`}
									/>
								</SpaceBetween>
							}
						>
							Workflow Tasks
						</Header>
					}
					empty={
						<Box textAlign="center" color="inherit">
							<b>No tasks</b>
							<Box padding={{ bottom: 's' }} variant="p" color="inherit">
								No tasks defined for this workflow.
							</Box>
						</Box>
					}
					pagination={<Pagination {...paginationProps} />}
					preferences={
						<CollectionPreferences
							{...collectionPreferences}
							preferences={preferences}
							onConfirm={({ detail }) => setPreferences(detail)}
						/>
					}
				/>
				{selectedTask && (
					<div className="taskDetailsCntr">
						<TaskDetailsReviewPanel
							taskDetails={selectedTask}
							showTaskDetails={() => setSelectedTask(undefined)}
						/>
					</div>
				)}
				{selectedValidations && (
					<ContentLayout>
						<Container
							header={
								<Header
									actions={
										<Button
											iconName="close"
											variant="icon"
											onClick={() => setSelectedValidations(null)}
										/>
									}
									variant="h3"
								>
									Validations
								</Header>
							}
						>
							<SpaceBetween size="s">
								<Box>
									<Box variant="awsui-key-label">Task</Box>
									<Box>{selectedValidationTaskName}</Box>
								</Box>
								{selectedValidations.map((validation: ValidationDefinition, idx: number) => (
									<Box key={idx}>
										{selectedValidations.length > 1 && idx > 0 && <Box padding={{ vertical: 'xs' }}><hr /></Box>}
										<SpaceBetween size="s">
											{selectedValidations.length > 1 && (
												<Box fontWeight="bold">{validation.validationName || validation.validationId || `Validation ${idx + 1}`}</Box>
											)}
											<Box>
												<Box variant="awsui-key-label">Validation ID</Box>
												<CopyableText textToCopy={validation.validationId || ''} label="Validation ID">
													{validation.validationId || '-'}
												</CopyableText>
											</Box>
											<Box>
												<Box variant="awsui-key-label">Type</Box>
												<Box>{validation.validationType || '-'}</Box>
											</Box>
											<Box>
												<Box variant="awsui-key-label">Description</Box>
												<Box>{validation.validationDescription || '-'}</Box>
											</Box>
											{validation.validationOperationConfig?.operationId && (
												<Box>
													<Box variant="awsui-key-label">Operation ID</Box>
													<CopyableText textToCopy={validation.validationOperationConfig.operationId} label="Operation ID">
														{validation.validationOperationConfig.operationId}
													</CopyableText>
												</Box>
											)}
											{validation.validationParameters && validation.validationParameters.length > 0 && (
												<Box>
													<Box variant="awsui-key-label">Attribute Mapping</Box>
													<ColumnLayout columns={2} variant="text-grid">
														{validation.validationParameters.map((param: any, pIdx: number) => (
															<Box key={pIdx} padding={{ vertical: 'xxs' }}>
																<Box fontWeight="bold">{param.validationParameterName || '-'}</Box>
																<Box>{param.validationParameterValue || '-'}</Box>
															</Box>
														))}
													</ColumnLayout>
												</Box>
											)}
										</SpaceBetween>
									</Box>
								))}
							</SpaceBetween>
						</Container>
					</ContentLayout>
				)}
			</Grid>
		</Container>
	);
}
